import { Card, CardContent } from '@/components/ui/card';
import { formatNumber } from '@/lib/formatNumber';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ProductIdentity } from '@/components/products';
import { apiFetch } from '@/lib/api';
import { useCallback, useEffect, useState } from 'react';

const MOVEMENT_TYPES = [
  'GOODS_RECEIPT',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'ADJUSTMENT_IN',
  'ADJUSTMENT_OUT',
  'REFILL_OUT',
  'WASTAGE',
];

interface Movement {
  id: string;
  createdAt: string;
  movementType: string;
  quantity: number;
  warehouse?: { name: string };
  product?: { id: string; name: string; sku: string };
  batch?: { batchNumber: string } | null;
  referenceType: string | null;
  referenceId: string | null;
}

export default function StockMovementsPage() {
  const [movements, setMovements] = useState<Movement[]>([]);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string }>>([]);
  const [warehouseId, setWarehouseId] = useState('all');
  const [movementType, setMovementType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    apiFetch('/api/v1/warehouses').then(async (res) => {
      if (res.ok) {
        const data = await res.json();
        setWarehouses(data.items ?? data ?? []);
      }
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ pageSize: '100' });
      if (warehouseId !== 'all') params.set('warehouseId', warehouseId);
      if (movementType !== 'all') params.set('movementType', movementType);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await apiFetch(`/api/v1/stock/movements?${params}`);
      if (res.ok) {
        const data = await res.json();
        setMovements(data.items ?? []);
      }
    } finally {
      setLoading(false);
    }
  }, [warehouseId, movementType, dateFrom, dateTo]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Stock Movements</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Audit ledger of every stock change.
        </p>
      </div>

      <Card className="mb-6">
        <CardContent className="grid gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label>Warehouse</Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Type</Label>
            <Select value={movementType} onValueChange={setMovementType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {MOVEMENT_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="hidden md:table-cell">Warehouse</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="hidden lg:table-cell">Batch</TableHead>
                <TableHead className="text-right">Qty</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Loading movements…
                  </TableCell>
                </TableRow>
              ) : movements.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    No movements match filters.
                  </TableCell>
                </TableRow>
              ) : (
                movements.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>{new Date(m.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="hidden md:table-cell">{m.warehouse?.name ?? '—'}</TableCell>
                    <TableCell>
                      {m.product ? (
                        <ProductIdentity
                          productId={m.product.id}
                          name={m.product.name}
                          sku={m.product.sku}
                          size="sm"
                          showSku={false}
                          popOut={false}
                        />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{m.movementType}</TableCell>
                    <TableCell className="hidden font-mono text-sm lg:table-cell">
                      {m.batch?.batchNumber ?? '—'}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {['TRANSFER_OUT', 'ADJUSTMENT_OUT', 'REFILL_OUT', 'WASTAGE'].includes(m.movementType)
                        ? `-${formatNumber(m.quantity)}`
                        : `+${formatNumber(m.quantity)}`}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
